import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { checkFeedUrls } from '../../services/api';
import type { UrlCheckResponse } from '../../services/api';
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  Button,
  Banner,
  TextField,
} from '@shopify/polaris';
import { ClipboardIcon } from '@shopify/polaris-icons';
import { formatRelativeOrDate, formatDateTime } from '../../utils/dateFormat';
import {
  useFeedStatus,
  useActivateFeed,
  useRegenerateFeed,
  useDeactivateFeed,
} from '../../hooks/useFeed';

/**
 * T102 [US2]: Feed dashboard component.
 * Feed URL display + copy, status indicator, regenerate button,
 * last generated timestamp.
 *
 * @see contracts/feed.md
 */
interface FeedDashboardProps {
  /** Opens the app embed setup video dialog. */
  onPlayEmbedVideo?: () => void;
}

export function FeedDashboard({ onPlayEmbedVideo }: FeedDashboardProps = {}) {
  const { data: feed, isLoading, isError } = useFeedStatus();
  const activate = useActivateFeed();
  const regenerate = useRegenerateFeed();
  const deactivate = useDeactivateFeed();

  const { data: urlCheck } = useQuery<UrlCheckResponse>({
    queryKey: ['feed-url-check'],
    queryFn: () => checkFeedUrls(),
    enabled: feed?.status === 'ACTIVE',
    staleTime: 60_000,
    retry: false,
  });

  const copyToClipboard = useCallback(async (text: string) => {
    await navigator.clipboard.writeText(text);
  }, []);

  if (isLoading) {
    return (
      <Card>
        <Text as="p" tone="subdued">Loading feed status…</Text>
      </Card>
    );
  }

  // Feed not activated yet
  if (isError || !feed) {
    return (
      <Card>
        <BlockStack gap="300">
          <Text as="h2" variant="headingMd">AI Product Feed</Text>
          <Text as="p" tone="subdued">
            Activate your AI-optimised product feed to make your catalog
            discoverable by AI platforms and search engines.
          </Text>
          <Button
            variant="primary"
            onClick={() => activate.mutate()}
            loading={activate.isPending}
          >
            Activate Feed
          </Button>
          {activate.isError && (
            <Banner tone="critical">
              {activate.error?.message ?? 'Failed to activate feed'}
            </Banner>
          )}
        </BlockStack>
      </Card>
    );
  }

  // Build URLs
  const storeDomain = feed.shopDomain;
  const storeBaseUrl = storeDomain ? `https://${storeDomain}/apps/zelium` : null;

  const storeUrls = storeBaseUrl
    ? {
        feed: `${storeBaseUrl}/feed/products.json`,
        index: `${storeBaseUrl}/feed/index.json`,
        llms: `${storeBaseUrl}/.well-known/llms.txt`,
        robots: `${storeBaseUrl}/feed/robots-hints.txt`,
        sitemap: `${storeBaseUrl}/feed/ai-sitemap.xml`,
      }
    : null;

  return (
    <BlockStack gap="400">
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h2" variant="headingMd">AI Product Feed</Text>
            <StatusBadge status={feed.status} />
          </InlineStack>

          {/* Metadata */}
          <InlineStack gap="400">
            <Text as="span" variant="bodySm" tone="subdued">
              {feed.totalProducts} products · {feed.totalPages} pages
            </Text>
            {feed.lastGeneratedAt && (
              <Text as="span" variant="bodySm" tone="subdued">
                Last generated: {formatRelativeOrDate(feed.lastGeneratedAt)}
              </Text>
            )}
            {feed.nextRegenerationAt && (
              <Text as="span" variant="bodySm" tone="subdued">
                Next: {formatDateTime(feed.nextRegenerationAt)}
              </Text>
            )}
          </InlineStack>

          {feed.status === 'GENERATING' && (
            <Banner tone="info">Feed is being generated. This may take a few minutes.</Banner>
          )}

          {feed.status === 'ERROR' && (
            <Banner tone="critical">
              Feed generation failed. Try regenerating.
            </Banner>
          )}

          {/* Actions */}
          <InlineStack gap="200">
            <Button
              onClick={() => regenerate.mutate()}
              loading={regenerate.isPending}
              disabled={feed.status === 'GENERATING'}
            >
              Regenerate
            </Button>
            <Button
              tone="critical"
              onClick={() => deactivate.mutate()}
              loading={deactivate.isPending}
            >
              Deactivate
            </Button>
          </InlineStack>
        </BlockStack>
      </Card>

      {/* Store Domain URLs */}
      {storeUrls && (
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">Store Domain URLs</Text>
            {urlCheck?.embedDetected === false && (
              <Banner tone="warning">
                <BlockStack gap="100">
                  <span>
                    These URLs only work when the <strong>Zelium AI Structured Data</strong> app
                    embed is enabled in your theme. Go to{' '}
                    <strong>Online Store → Themes → Customize → App embeds</strong> and
                    toggle it on.
                  </span>
                  {onPlayEmbedVideo && (
                    <div>
                      <Button variant="plain" onClick={onPlayEmbedVideo}>
                        Watch how to enable it
                      </Button>
                    </div>
                  )}
                </BlockStack>
              </Banner>
            )}
            <UrlRow
              label="Product Feed"
              url={storeUrls.feed}
              onCopy={() => copyToClipboard(storeUrls.feed)}
            />
            <UrlRow
              label="Feed Index"
              url={storeUrls.index}
              onCopy={() => copyToClipboard(storeUrls.index)}
            />
            <UrlRow
              label="llms.txt"
              url={storeUrls.llms}
              onCopy={() => copyToClipboard(storeUrls.llms)}
            />
            <UrlRow
              label="robots-hints.txt"
              url={storeUrls.robots}
              onCopy={() => copyToClipboard(storeUrls.robots)}
            />
            <UrlRow
              label="ai-sitemap.xml"
              url={storeUrls.sitemap}
              onCopy={() => copyToClipboard(storeUrls.sitemap)}
            />
          </BlockStack>
        </Card>
      )}


    </BlockStack>
  );
}

/** Single URL row with label, read-only field, and copy button. */
function UrlRow({
  label,
  url,
  onCopy,
}: {
  label: string;
  url: string;
  onCopy: () => void;
}) {
  return (
    <TextField
      label={label}
      value={url}
      readOnly
      autoComplete="off"
      connectedRight={
        <Button icon={ClipboardIcon} onClick={onCopy} accessibilityLabel={`Copy ${label} URL`} />
      }
    />
  );
}

function StatusBadge({ status }: { status: string }) {
  const tone = status === 'ACTIVE' ? 'success'
    : status === 'GENERATING' ? 'attention'
    : status === 'ERROR' ? 'critical'
    : 'info';
  return <Badge tone={tone}>{status}</Badge>;
}
